import Router from 'next/router';
import { gql } from '@soundxyz/gql-string';
import type { LikeButtonSource } from '@soundxyz/utils/analytics';
import { useAuth } from '@context/auth/base';
import { triggerLogin } from '@context/auth/store';
import { ToastContext } from '@context/toast';
import { RefetchOnComplete } from '@graphql/helpers/effects';
import {
  AllUserPlaylistsDocument,
  LikePlaylistDocument,
  PlaylistPageNewDocument,
  UnlikePlaylistDocument,
  useMutation,
} from '@graphql/reactQuery';
import { trackClickEvent } from '@lib/analytics';
import { setHideExpandedPlayer } from '@store/audio/AudioMeta';
import { webappUriPath } from '@utils/url';
import { useLikeStatus } from './useLikeStatus';

/*#__PURE__*/ gql(/* GraphQL */ `
  mutation LikePlaylist($id: UUID!) {
    likeShelf(input: { shelfId: $id }) {
      __typename
      ... on MutationLikeShelfSuccess {
        data {
          id
          likes
        }
      }
      ... on Error {
        message
      }
    }
  }

  mutation UnlikePlaylist($id: UUID!) {
    unlikeShelf(input: { shelfId: $id }) {
      __typename
      ... on MutationUnlikeShelfSuccess {
        data {
          id
          likes
        }
      }
      ... on Error {
        message
      }
    }
  }
`);

RefetchOnComplete({
  trigger: [LikePlaylistDocument, UnlikePlaylistDocument],
  refetch: [AllUserPlaylistsDocument, PlaylistPageNewDocument],
});

export const useLikePlaylist = ({ id, source }: { id: string; source: LikeButtonSource }) => {
  const { authUser } = useAuth();

  const { openToast } = ToastContext.useContainer();
  const { refetch, isLiked, setLiked } = useLikeStatus({ id });

  const { mutateAsync: likePlaylist } = useMutation(LikePlaylistDocument, {
    onSuccess() {
      refetch();
    },
  });
  const { mutateAsync: unlikedPlaylist } = useMutation(UnlikePlaylistDocument, {
    onSuccess() {
      refetch();
    },
  });

  const onClick = async () => {
    if (!authUser?.id) {
      triggerLogin();
      return;
    }

    if (!isLiked) {
      // make it snappy and update the UI first
      setLiked(true);
      trackClickEvent({
        event: 'Like Clicked',
        properties: {
          sourceComponent: source,
          associationId: id,
          associationType: 'Playlist',
        },
      });
      await likePlaylist(
        { id },
        {
          onSuccess: ({ data }) => {
            if (data?.likeShelf.__typename !== 'MutationLikeShelfSuccess') {
              // rollback state
              setLiked(false);
              // TODO: linear.app/sound/issue/ENG-9185/add-missing-observability-in-fe
              return openToast({
                text: data?.likeShelf.message || 'Something went wrong while liking this Playlist.',
                variant: 'error',
              });
            }
            openToast({
              text: 'You liked this Playlist.',
              ctaText: 'View your Liked Playlists.',
              ctaOnClick: () => {
                Router.push(webappUriPath(authUser.webappUri));
                setHideExpandedPlayer(true);
              },
              variant: 'success',
            });
          },
        },
      );
    } else {
      // make it snappy and update the UI first
      setLiked(false);
      trackClickEvent({
        event: 'Unlike Clicked',
        properties: {
          sourceComponent: source,
          associationId: id,
          associationType: 'Playlist',
        },
      });
      await unlikedPlaylist(
        { id },
        {
          onSuccess: ({ data }) => {
            if (data?.unlikeShelf.__typename !== 'MutationUnlikeShelfSuccess') {
              // rollback state
              setLiked(true);
              openToast({
                text:
                  data?.unlikeShelf.message || 'Something went wrong while unliking this Playlist.',
                variant: 'error',
              });
              return;
            }
            openToast({
              text: 'You unliked this Playlist.',
              variant: 'success',
            });
          },
        },
      );
    }
  };

  return {
    /** status of release */
    isLiked,
    /** callback handler that will take care of liking/unliking a playlist */
    onClick,
  };
};
